/**
 * `ColumnDef` list for the global invoice table. Columns mirror the legacy
 * `getInvoiceListColumns` core subset (code link · customer · service · status ·
 * type · sale type · amounts · apply range · due · payment) built on the shared
 * invoice cell renderers + `src/lib/format` formatters.
 */
import type { ColumnDef } from '@vnetwork/ui/data';
import type { InvoiceRow } from '@/api/invoices';
import {
  appliedRangeCell,
  dateCell,
  invoiceAccountManagersCell,
  invoiceCodeCell,
  invoiceCustomerCell,
  invoiceExporterCodeCell,
  invoiceMailCountCell,
  invoiceSaleTypeCell,
  invoiceServiceCell,
  invoiceStatusCell,
  invoiceTypeCell,
  moneyCell,
} from './invoice-cell-renderers';

export type InvoiceCol = ColumnDef<InvoiceRow, unknown>;

const rightHeader = (label: string) => () => (
  <span style={{ display: 'block', textAlign: 'right' }}>{label}</span>
);

export const invoiceColumns: InvoiceCol[] = [
  {
    id: 'invoice_code',
    header: 'Invoice',
    cell: ({ row }) => invoiceCodeCell(row.original),
  },
  {
    id: 'customer',
    header: 'Customer',
    cell: ({ row }) => invoiceCustomerCell(row.original),
  },
  {
    id: 'service',
    header: 'Service',
    cell: ({ row }) => invoiceServiceCell(row.original),
  },
  {
    id: 'invoice_status',
    header: 'Status',
    cell: ({ row }) => invoiceStatusCell(row.original.invoice_status),
  },
  {
    id: 'invoice_type',
    header: 'Type',
    cell: ({ row }) => invoiceTypeCell(row.original.invoice_type),
  },
  {
    id: 'invoice_sale_type',
    header: 'Sale Type',
    cell: ({ row }) => invoiceSaleTypeCell(row.original.invoice_sale_type),
  },
  {
    id: 'invoice_amount',
    header: rightHeader('Amount'),
    cell: ({ row }) => moneyCell(row.original.invoice_amount),
  },
  {
    id: 'invoice_vat',
    header: rightHeader('VAT'),
    cell: ({ row }) => moneyCell(row.original.invoice_vat),
  },
  {
    id: 'invoice_total',
    header: rightHeader('Total'),
    cell: ({ row }) => moneyCell(row.original.invoice_total),
  },
  {
    id: 'account_managers',
    header: 'Account Mgrs',
    cell: ({ row }) => invoiceAccountManagersCell(row.original),
  },
  {
    id: 'invoice_exporter_code',
    header: 'Exporter Code',
    cell: ({ row }) => invoiceExporterCodeCell(row.original),
  },
  {
    id: 'invoice_mail_count',
    header: rightHeader('Emails'),
    cell: ({ row }) => invoiceMailCountCell(row.original),
  },
  {
    id: 'invoice_apply_range',
    header: rightHeader('Applied Period'),
    cell: ({ row }) =>
      appliedRangeCell(row.original.invoice_apply_startdate, row.original.invoice_apply_enddate),
  },
  {
    id: 'invoice_duedate',
    header: rightHeader('Due'),
    cell: ({ row }) => dateCell(row.original.invoice_duedate),
  },
  {
    id: 'invoice_payment_at',
    header: rightHeader('Paid At'),
    cell: ({ row }) => dateCell(row.original.invoice_payment_at),
  },
];
